/**
 * The ENTITY READ CONTRACT — the client half (#2759 part A, ADR F-96).
 *
 * Every registered entity is read through exactly one PROVIDER, and the SDK's
 * `EntityHandle` (and `raw()`) resolve it from the registry row, so Console,
 * Portal and MCP inherit the route with no branch of their own:
 *
 *   · `direct_table`     — the ordinary RLS read of the table;
 *   · `governed_subject` — `read_audit = 'subject'`: `core.subject_*`, which
 *                          receipt every row they return (`subject.ts`);
 *   · `module_door`      — the entity's MODULE reads it through its own
 *                          SECURITY DEFINER doors, reached only via the core
 *                          INVOKER dispatchers `core.entity_page / entity_get /
 *                          entity_get_many / entity_lookup / entity_count`
 *                          (core 2026101115). Its physical table may be closed
 *                          to every API role.
 *
 * A module-door read is written in the DECLARED capabilities of its contract:
 * filter fields and their operators, sort fields, search, a page bound. The
 * translation is `subject.ts`'s `parseGovernedRead` — the same parser, so the
 * two governed routes can never disagree about what a filter means — followed
 * by a check against what the module DECLARED. Anything else is a typed
 * `EntityReadUnsupportedError` with NOTHING sent (rule 4: never approximate;
 * dropping a filter returns more rows than were asked for).
 *
 * The server re-checks everything (22023) and computes the paging envelope
 * itself: `next` is a continuation iff more rows exist, `total` is the
 * declared count, or proved by a short last page, or null.
 */
import type { Row, SelectOptions } from './transport.js'
import {
  parseGovernedRead, project, subjectLeafSupported, leavesToSubjectFilters, sortToSubject,
  type SubjectClause, type SubjectFilterTranslation,
} from './subject.js'
import type { FilterLeaf } from './filters.js'

export type ReadProvider = 'direct_table' | 'governed_subject' | 'module_door'

/** What a module-door contract declares (and core derives), as `core.entity_read_contract_of` publishes it. */
export interface EntityReadCapabilities {
  page: boolean
  get: boolean
  get_many: boolean
  /** A lookup door exists: titles by id (picker search is `lookup_search`). */
  titles: boolean
  /** A count door exists, so `total` is exact on every page. */
  count: boolean
  total: 'exact' | 'none'
  paging: ('offset' | 'continuation')[]
  /** field → the operators declared for it (eq | in | is_null | not_null). */
  filter: Record<string, string[]>
  sort: string[]
  /** PAGE search: `query.search` on the page and count doors (`toEntityQuery`). */
  search: boolean
  /**
   * PICKER search: words through the lookup door (`core.entity_lookup`), core
   * 0.436.0 (#2915 L). Absent on an older deployment, where it follows `search`
   * — read it through `lookupSearchDeclared`, never directly.
   */
  lookup_search?: boolean
  /** Contract v1 has no grouping operation: always false. */
  grouping: boolean
  max_page: number
}

/** The published read contract of one entity (`describe_schema().entities[].read_contract`). */
export interface EntityReadContract {
  provider: ReadProvider
  version: number
  /**
   * Can a list of this entity be drawn for a signed-in caller at all? False for
   * a table no API role may read and no contract reads (or a contract whose
   * door has drifted). Navigation and record-open read THIS, never the
   * existence of a view or a title.
   */
  readable: boolean
  capabilities?: EntityReadCapabilities
}

/** The refusal: what the read asked that the entity's declared contract cannot express. */
export class EntityReadUnsupportedError extends Error {
  /** Stable machine marker — survives bundling/duplication where `instanceof` does not. */
  readonly code = 'entity_read_unsupported'
  constructor(
    readonly entity: string,
    readonly feature: 'filter' | 'order' | 'limit' | 'offset' | 'columns' | 'where' | 'search' | 'after',
    readonly detail: string,
  ) {
    super(
      `'${entity}' is read through its module's governed door. That door cannot express this read `
      + `(${feature}: ${detail}), so nothing was sent.`,
    )
    this.name = 'EntityReadUnsupportedError'
  }
}

/** Structural check for the refusal above — works across package copies. */
export function isEntityReadUnsupported(e: unknown): e is EntityReadUnsupportedError {
  return typeof e === 'object' && e !== null && (e as { code?: unknown }).code === 'entity_read_unsupported'
}

/**
 * Which provider reads this entity. An explicit published contract wins; a
 * registry row without one (a deployment older than core 0.427.0, or a host
 * that read `core.entity_type` directly) resolves exactly as before:
 * `read_audit = 'subject'` → the subject doors, anything else → the table.
 */
export function resolveReadProvider(info: { read_audit?: string | null; read_contract?: EntityReadContract | null } | null | undefined): ReadProvider {
  const p = info?.read_contract?.provider
  if (p === 'module_door' && info?.read_contract?.capabilities) return 'module_door'
  if (p === 'governed_subject' || info?.read_audit === 'subject') return 'governed_subject'
  return 'direct_table'
}

/**
 * Does the contract declare PICKER search (#2915 L)? An absent `lookup_search`
 * (a deployment before core 0.436.0) follows `search`, as the server does; an
 * explicit false never falls back to true.
 */
export function lookupSearchDeclared(caps: EntityReadCapabilities | null | undefined): boolean {
  return (caps?.lookup_search ?? caps?.search) === true
}

/** Extra read options only a governed provider understands. Refused, never ignored, elsewhere. */
export interface EntityReadOptions extends SelectOptions {
  /** Free-text search, where the contract declares it. */
  search?: string
  /** A continuation a previous page returned as `next`. */
  after?: string
}

/** A module-door read, already in the contract's vocabulary. */
export interface EntityQuery {
  filter: SubjectClause[]
  order: { field: string; descending: boolean } | null
  search: string | null
  limit: number
  offset?: number
  after?: string
  /** A projection the CLIENT applies; null = every column the door returns. */
  columns: string[] | null
}

/** Express a read in the entity's DECLARED vocabulary — or refuse, with nothing sent. */
export function toEntityQuery(entity: string, caps: EntityReadCapabilities, opts: EntityReadOptions | undefined): EntityQuery {
  const refuse = (feature: EntityReadUnsupportedError['feature'], detail: string) =>
    new EntityReadUnsupportedError(entity, feature, detail)
  const q = parseGovernedRead(opts, refuse, caps.max_page)
  for (const c of q.filter) {
    const ops = caps.filter[c.field]
    if (!ops) throw refuse('filter', `${c.field} is not a declared filter field`)
    if (!ops.includes(c.op)) throw refuse('filter', `${c.field} ${c.op} is not declared (declared: ${ops.join(', ')})`)
  }
  if (q.orderBy !== null && !caps.sort.includes(q.orderBy)) {
    throw refuse('order', `${q.orderBy} is not a declared sort field`)
  }
  let search: string | null = null
  if (opts?.search != null) {
    if (!caps.search) throw refuse('search', 'search is not declared')
    const s = opts.search.trim()
    if (s.length < 1 || s.length > 200) throw refuse('search', 'search must be 1..200 characters')
    search = s
  }
  if (opts?.after != null && opts.offset != null && opts.offset !== 0) {
    throw refuse('after', 'a continuation and an offset are exclusive')
  }
  return {
    filter: q.filter,
    order: q.orderBy === null ? null : { field: q.orderBy, descending: q.descending },
    search,
    limit: q.limit,
    ...(opts?.after != null ? { after: opts.after } : { offset: q.offset }),
    columns: q.columns,
  }
}

/** The `p_query` argument `core.entity_page` takes for a query this module produced. */
export function entityPageArgs(entity: string, q: EntityQuery): Row {
  const query: Row = { filter: q.filter, order: q.order, search: q.search, limit: q.limit }
  if (q.after != null) query.after = q.after
  else query.offset = q.offset ?? 0
  return { p_entity: entity, p_query: query }
}

/** The envelope `core.entity_page` returns. */
export interface EntityPageEnvelope<T> { rows: T[]; total: number | null; next: string | null }

/** Apply the query's projection to an envelope's rows. */
export function projectEnvelope<T>(env: EntityPageEnvelope<Row> | null | undefined, q: EntityQuery): EntityPageEnvelope<T> {
  return {
    rows: project<T>(Array.isArray(env?.rows) ? env!.rows : [], q.columns),
    total: typeof env?.total === 'number' ? env.total : null,
    next: typeof env?.next === 'string' ? env.next : null,
  }
}

/**
 * Refuse a governed-only option on a provider that does not understand it —
 * the PostgREST read would IGNORE `search`/`after` and return more than asked.
 */
export function assertPlainRead(entity: string, opts: EntityReadOptions | undefined): void {
  if (opts?.search != null) throw new EntityReadUnsupportedError(entity, 'search', 'this entity declares no governed search')
  if (opts?.after != null) throw new EntityReadUnsupportedError(entity, 'after', 'this entity pages by offset')
}

// ── the HOST's half: a saved view, for whichever governed provider reads it ──
//
// A host's grid speaks `@solum/rules` leaves and a sort spec. For a governed
// entity — subject doors or a module door — the faithful subset is the SAME
// (`subject.ts` decides it per kind); a module door narrows it further to what
// its module DECLARED. These three functions are what a host asks, so its
// column menu, its translation and the SDK's refusal are one answer.

/** The door operator a menu leaf maps onto, or null where it has none. */
function doorOpOfLeaf(op: string): string | null {
  return op === 'eq' ? 'eq' : op === 'in' ? 'in' : op === 'empty' ? 'is_null' : null
}

/** The door operator a PostgREST filter value expresses (the subset `leavesToSubjectFilters` emits). */
function doorOpOfFilter(value: string): string | null {
  if (value === 'is.null') return 'is_null'
  if (value === 'not.is.null') return 'not_null'
  if (value.startsWith('eq.')) return 'eq'
  if (value.startsWith('in.(')) return 'in'
  return null
}

/**
 * May a column menu offer this leaf on this field, for an entity read through
 * a governed provider? `capabilities` null = the subject doors (every
 * registered field); otherwise the module's DECLARED field and operator.
 */
export function governedLeafSupported(op: string, kind: string | undefined, field: string, capabilities: EntityReadCapabilities | null): boolean {
  if (!subjectLeafSupported(op, kind)) return false
  if (!capabilities) return true
  const door = doorOpOfLeaf(op)
  return door != null && (capabilities.filter[field] ?? []).includes(door)
}

/**
 * Saved-view leaves → filters a governed provider answers faithfully, or the
 * FIRST leaf it cannot (all or nothing, like `leavesToSubjectFilters`, which
 * it extends with the module's declared vocabulary).
 */
export function leavesToGovernedFilters(
  leaves: FilterLeaf[],
  opts: { kindOf?: (field: string) => string | undefined; binds?: Record<string, unknown>; capabilities?: EntityReadCapabilities | null } = {},
): SubjectFilterTranslation {
  const t = leavesToSubjectFilters(leaves, opts)
  const caps = opts.capabilities
  if (!t.ok || !caps) return t
  for (const f of t.filters) {
    const door = doorOpOfFilter(f.value)
    if (door == null || !(caps.filter[f.key] ?? []).includes(door)) {
      return { ok: false, field: f.key, op: door, reason: 'a condition its module does not offer on this field' }
    }
  }
  return t
}

/** A saved-view sort → the one ordering a governed provider applies, and what it could not. */
export function sortToGoverned(sortSpec: unknown[] | undefined, capabilities: EntityReadCapabilities | null): { order: string | undefined; dropped: string[] } {
  const s = sortToSubject(sortSpec)
  if (!capabilities || s.order === undefined) return s
  const first = s.order.split('.')[0]!
  if (capabilities.sort.includes(first)) return s
  return { order: undefined, dropped: [first, ...s.dropped] }
}

/** The structural check hosts use for EITHER governed refusal (subject or module door). */
export function isGovernedReadUnsupported(e: unknown): e is { code: string; message: string } {
  const code = typeof e === 'object' && e !== null ? (e as { code?: unknown }).code : undefined
  return code === 'entity_read_unsupported' || code === 'subject_read_unsupported'
}
